import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent, useState } from 'react'
import { Color, Colors, ColorUtil } from '~/assets/style/colors'
import { WithClassName } from '~/types/utils'

type CatalogLogoProps = WithClassName & {
  baseCDNUrl: string
  /** Keys both logo lookups. */
  code: string
  /** Turned into a monogram when neither logo loads. */
  name: string
}

// A handful of tokens already used elsewhere on the card, cycled by name so the same company always
// lands on the same tint without needing a colour stored anywhere.
const MONOGRAM_COLORS: Color[] = [Colors.Ming, Colors.Kaiminus, Colors.Lava, Colors.Violet, Colors.Milka]

const monogramColor = (seed: string): Color => {
  const hash = [...seed].reduce((total, char) => total + char.charCodeAt(0), 0)
  return MONOGRAM_COLORS[hash % MONOGRAM_COLORS.length]
}

const monogramInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('')

type LogoStage = 'square' | 'legacy' | 'monogram'

const nextStage = (stage: LogoStage): LogoStage => (stage === 'square' ? 'legacy' : 'monogram')

/**
 * The catalog card's logo, with a fallback chain: the new square asset, then the legacy wordmark, then
 * a monogram of the company's name.
 *
 * Not every bidder has the new square logo uploaded yet, and a partner riding another company's
 * adapter code (`disableAliasing: false`, `code` shared with the adapter it rides) may have no logo of
 * its own at all under either path. That is exactly why this never borrows a substitute image — the
 * monogram is neutral, a wrong company's logo is not.
 */
export const CatalogLogo: FunctionComponent<CatalogLogoProps> = ({ baseCDNUrl, code, name, className }) => {
  const [stage, setStage] = useState<LogoStage>('square')

  if (stage === 'monogram') {
    // Sized to fill the tile it sits in rather than to match the wordmarks: a monogram is standing in for
    // a logo, and a small pair of letters in a large empty square reads as a failed image instead.
    const color = monogramColor(code)
    return (
      <div
        className={cn('flex size-full items-center justify-center text-[28px] leading-none font-semibold', className)}
        style={{ backgroundColor: ColorUtil.hexOpacity(color, 0.12), color }}
        /* Decorative: it stands in for a logo, and the name it is drawn from is rendered right beside
           it. Without this the letter joins the name in the accessibility tree and in extracted text —
           "K Kargo", "O Ozone" — reading as a stray initial rather than as a missing image. */
        aria-hidden={'true'}
      >
        {monogramInitials(name)}
      </div>
    )
  }

  const path = stage === 'square' ? `integrations/logos/${code}.png` : `integrations/${code}.png`

  return <img className={cn('max-h-[45px] max-w-[50px] object-contain', className)} src={`${baseCDNUrl}/${path}`} alt={name} onError={() => setStage(nextStage(stage))} />
}
